import { Button, Card, CardContent, Typography, useTheme } from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { API_URL } from '../../api/apis';
import { Svgs } from '../../assets/svg';
import { Page, Spacer, Row, DataTable } from '../../components';
import { useLoading } from '../../hooks/useLoading';
import { useLicenseAgreements } from '../../hooks/useLicenseAgreements';
import { sortByDate } from '../../helpers/sortUtils';
import { isFreemiumPricebook } from '../../helpers/pricebookUtils';
import { LicenseInfo } from '../../models/OrganizationData';
import { PricebookBundle } from '../../models/PricebookData';
import { Toast } from '../../models/Toast';
import { ToastbarContext } from '../../App';
import LearnMoreDrawer from '../../sign-up/license/components/LearnMoreDrawer';
import PurchaseLicensesDrawer from './PurchaseLicensesDrawer';
import LicenseDeleteModal from './LicenseDeleteModal';
import UnpaidInvoiceCard from '../payment/UnpaidInvoiceCard';
import { InvalidateAgreement } from '../../data/license/commands/InvalidateAgreement';
import { useCommands } from '../../helpers/useCommands';
import { PaidByEnum } from '../../models/LicenseData';

const artIconHeader = {
  title: '',
  label: '_artIcon',
  sortable: false,
  cellSx: { width: '42px', minWidth: '42px', maxWidth: '42px', flex: 'none', pl: 0, pr: 0 },
};
const productHeader = { title: 'Product', label: 'product', flex: 3, cellSx: { pr: '40px' } };
const startDateHeader = { title: 'Start Date', label: 'startDate', flex: 2, cellSx: { pr: '20px' } };

const activeHeaders = [
  artIconHeader,
  productHeader,
  startDateHeader,
  { title: '', label: '_spacer', flex: 2, sortable: false },
];
const expiredHeaders = [
  artIconHeader,
  productHeader,
  startDateHeader,
  { title: 'End Date', label: 'endDate', flex: 2 },
];

const rowSpacingSx = {
  '& .MuiTableCell-root': { py: '12px' },
};

const LicenseAgreement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const commands = useCommands();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();

  const [modalOpen, setModalOpen] = useState(false);
  const [activeData, setActiveData] = useState(undefined);
  const [purchaseDrawerOpen, setPurchaseDrawerOpen] = useState<boolean>(false);
  const [chosenPricebook, setChosenPricebook] = useState<PricebookBundle | null>(null);
  const [detailsPricebook, setDetailsPricebook] = useState<PricebookBundle | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);

  const openDetails = useCallback((pricebook: PricebookBundle) => {
    setDetailsPricebook(pricebook);
    setDetailsDrawerOpen(true);
  }, []);

  const {
    licenseAgreements,
    paidBy,
    meteredPayment,
    zeroActive,
    pendingAgreement,
    groupFeatureMapData,
    packagesAvailable,
    failedInvoices,
    activeTableData,
    expiredTableData,
    tableDataReady,
  } = useLicenseAgreements(openDetails);

  const handleEditLicenses = (row: any) => {
    const selectedPricebook = licenseAgreements?.find(agreement => agreement.id === row.id);
    if (!selectedPricebook) {
      setToast(
        new Toast({
          message: `Error finding pricebook`,
          severity: 'error',
          open: true,
        }),
      );
      return;
    }
    setChosenPricebook(selectedPricebook.pricebookBundleDto);
    setPurchaseDrawerOpen(true);
  };

  const addPackage = () => {
    navigate('/addPackage', { state: { origin: 'license' } });
  };

  const viewLicense = async (row: any) => {
    const docId = row.id;
    const licenseAgreement = licenseAgreements?.find(doc => doc.id === docId);
    if (!licenseAgreement) {
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Error locating document. Please contact us if the problem persists.',
        }),
      );
      return;
    }
    const docUri = API_URL + 'license-agreement/doc?docId=' + licenseAgreement.id;
    window.open(`${docUri}`, '_blank');
  };

  const invalidateLicense = async (row: any) => {
    const licenseAgreement = licenseAgreements?.find(doc => doc.id === row.id);
    try {
      setLoading(true);
      if (licenseAgreement) {
        await commands.execute(InvalidateAgreement, licenseAgreement.id);
      } else {
        throw new Error('Could not find license agreement with id: ' + row.id);
      }
      setToast(
        new Toast({
          severity: 'success',
          open: true,
          message: 'License agreement canceled!',
        }),
      );
    } catch (err) {
      const productName = licenseAgreement?.pricebookBundleDto?.pricebook?.pricebookName;
      setToast(
        new Toast({
          message: productName
            ? `${productName} could not be removed`
            : 'License agreement could not be removed',
          severity: 'error',
          open: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (row: LicenseInfo) => {
    const agreement = licenseAgreements?.find(a => a.id === row.id);
    if (agreement) openDetails(agreement.pricebookBundleDto);
  };

  const onCancelPress = (row: any) => {
    setModalOpen(true);
    setActiveData(row);
  };

  return (
    <Page title='License agreement'>
      <LicenseDeleteModal
        onSave={invalidateLicense}
        activeData={activeData}
        open={modalOpen}
        setOpen={setModalOpen}
      />
      <Spacer height='xs' />
      {failedInvoices?.map(invoice => (
        <>
          <UnpaidInvoiceCard subscription={invoice} />
          <Spacer height='xs' />
        </>
      ))}
      <Row style={{ justifyContent: 'space-between' }}>
        <Typography variant='subtitle1' color='secondary.main'>
          Active license agreement(s)
        </Typography>
        {(zeroActive || paidBy !== PaidByEnum.Org) &&
          packagesAvailable &&
          groupFeatureMapData?.canAddPackage && (
            <Button className='add-package' onClick={addPackage} color='primary'>
              Add a package
            </Button>
          )}
      </Row>
      <Spacer height='xs' />
      {!packagesAvailable && (
        <>
          <Card variant='ghost' color='primary'>
            <CardContent className='pad20'>
              <Row style={{ justifyContent: 'center' }}>
                <Typography variant='h3' color='secondary.main'>
                  You’ve added all available packages!
                </Typography>
              </Row>
            </CardContent>
          </Card>
          <Spacer height='sm' />
        </>
      )}
      {!zeroActive &&
        (paidBy === PaidByEnum.Org ? (
          <Row style={{ alignItems: 'center' }}>
            <Svgs.IconOrgPaysLarge />
            <Spacer width='xxs' />
            <Typography color='secondary.main' variant='p20SemiBold'>
              Organization pays
            </Typography>
          </Row>
        ) : (
          <Row style={{ alignItems: 'center' }}>
            <Svgs.IconMemberPaysLarge />
            <Spacer width='xxs' />
            <Typography color='secondary.main' variant='p20SemiBold'>
              Member pays
            </Typography>
          </Row>
        ))}
      <Spacer height='xs' />
      <DataTable
        data={activeTableData}
        headers={activeHeaders}
        type='licenseAgreement'
        innerTableSx={rowSpacingSx}
        lastColumnElement='menu'
        ellipsesItems={[
          {
            label: 'Edit licenses',
            onClick: handleEditLicenses,
            disabled: typeof pendingAgreement !== 'undefined',
            showFn: () => paidBy === PaidByEnum.Org && !meteredPayment,
          },
          {
            label: 'View agreement',
            onClick: viewLicense,
          },
          {
            label: 'View details',
            onClick: viewDetails,
          },
          {
            label: 'Cancel contract',
            onClick: onCancelPress,
            sx: { color: theme.palette.error.dark },
            showFn: () => groupFeatureMapData?.organizationAllowInvalidateLicenseAgreement ?? false,
          },
        ]}
        emptyStateText={`You don't have any active license agreements.`}
        loading={!tableDataReady}
        customSort={[
          { column: 'startDate', function: (a, b) => sortByDate(a._startDate, b._startDate) },
        ]}
      />
      <Spacer height='sm' />
      <DataTable
        title='Expired license agreement(s)'
        data={expiredTableData}
        type='licenseAgreement'
        headers={expiredHeaders}
        innerTableSx={rowSpacingSx}
        lastColumnElement='menu'
        ellipsesItems={[
          {
            label: 'View agreement',
            onClick: viewLicense,
          },
          {
            label: 'View details',
            onClick: viewDetails,
          },
        ]}
        emptyStateText={`You don't have any expired license agreements.`}
        loading={!tableDataReady}
        customSort={[
          { column: 'startDate', function: (a, b) => sortByDate(a._startDate, b._startDate) },
          { column: 'endDate', function: (a, b) => sortByDate(a._endDate, b._endDate) },
        ]}
      />

      {chosenPricebook && (
        <PurchaseLicensesDrawer
          open={purchaseDrawerOpen}
          onClose={() => setPurchaseDrawerOpen(false)}
          pricebook={chosenPricebook}
        />
      )}

      {detailsPricebook && (
        <LearnMoreDrawer
          pricebookBundle={detailsPricebook}
          drawerOpen={detailsDrawerOpen}
          setDrawerOpen={setDetailsDrawerOpen}
          freemium={isFreemiumPricebook(detailsPricebook.pricebook)}
        />
      )}
    </Page>
  );
};

export default LicenseAgreement;
